import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { Alert, Button, Col, Container, Form, Pagination, Row, Spinner, Table } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import { AccountContext } from "./AccountLayout";
import { SessionContext } from "../App";
import Admin from "../classes/Admin";
import Auth from "../classes/Auth";
import LMS from "../classes/LMS";
import LMSUser from "../classes/LMSUser";
import ConfirmationBox from "./components/ConfirmationBox";
import EditLMSView from "./EditLMSView";
import strings from "../strings";
import "../styles/ShowUsersView.css";

const tableColumnNames = [
	strings.userID,
	strings.threeDeeAppId,
	strings.actions
];

function ShowUsersView() {
	const navigate = useNavigate();
	const [setAccountContext] = useContext(AccountContext);
	const setLoggedIn = useContext(SessionContext);
	const {lmsID} = useParams();
	const [isLoading, setIsLoading] = useState(true);
	const [showEditLMSModal, setShowEditLMSModal] = useState(false);
	const [showDeleteLMSModal, setShowDeleteLMSModal] = useState(false);
	const [deleteUserModalState, setDeleteUserModalState] = useState(false);
	const [alert, setAlert] = useState("");
	const [alertType, setAlertType] = useState("");
	const [name, setName] = useState("");
	const [url, setURL] = useState("");
	const [type, setType] = useState("");
	const [adminID, setAdminID] = useState(null);
	const [adminLabel, setAdminLabel] = useState("");
	const [searchTerm, setSearchTerm] = useState("");
	const [users, setUsers] = useState([]);
	const [foundUsers, setFoundUsers] = useState(users);
	const itemsPerPage = 5;
	const [currentPage, setCurrentPage] = useState(1);
	const [totalPages, setTotalPages] = useState(Math.ceil(users.length / itemsPerPage));
	const searchTermRef = useRef(searchTerm);
	const startIndex = (currentPage - 1) * itemsPerPage;
	const endIndex = startIndex + itemsPerPage;
	const usersOnCurrentPage = foundUsers.slice(startIndex, endIndex);
	const session = Auth.getSession();
	const role = (session && session.role) ? session.role : "";

	const back = () => navigate(-1);

	const logout = useCallback(() => Auth.logout().then(() => setLoggedIn(false)), [setLoggedIn]);

	const search = (e) => setSearchTerm(e.target.value);

	const closeAlert = () => {
		setAlert("");
		setAlertType("");
	};

	const removeLMS = () => {
		LMS.delete(lmsID)
			.then((result) => {
				if (result) {
					switch (result.status) {
						case 200:
							back();
							break;
						case 401:
							logout();
							break;
						case 403:
							setAlertType("danger");
							setAlert(strings.unauthorisedRequest);
							break;
						case 400:
						case 404:
						case 422:
							setAlertType("warning");
							setAlert(strings.requestFailed);
							break;
						case 500:
							setAlertType("danger");
							setAlert(strings.serverError);
							break;
						default:
					}
				}
			});
	};

	const removeLMSUser = (id, userID, threeDeeAppId) => {
		LMSUser.delete(lmsID, id, userID, threeDeeAppId)
			.then((result) => {
				if (result) {
					switch (result.status) {
						case 200:
							setUsers(users.filter((user) => (user.id !== id)));
							break;
						case 400:
						case 404:
							setAlertType("warning");
							setAlert(strings.requestFailed);
							break;
						case 401:
							logout();
							break;
						case 403:
							setAlertType("danger");
							setAlert(strings.unauthorisedRequest);
							break;
						case 422:
							setAlertType("warning");
							setAlert(strings.requestFailed);
							break;
						case 500:
							setAlertType("danger");
							setAlert(strings.serverError);
							break;
						default:
					}
				}
			});
	};

	useEffect(() => {
		LMS.get(lmsID)
			.then((result) => {
				if (result) {
					switch (result.status) {
						case 200:
							setName(result.data.name ?? "");
							setURL(result.data.baseURL ?? "");
							setType(result.data.statementsType ?? "");
							setAdminID(result.data.idAdmin ?? "");
							break;
						case 401:
							logout();
							break;
						case 403:
							setAlertType("danger");
							setAlert(strings.unauthorisedRequest);
							break;
						case 404:
							setAlertType("warning");
							setAlert(strings.requestFailed);
							break;
						case 500:
							setAlertType("danger");
							setAlert(strings.serverError);
							break;
						default:
					}
				}
			});

		LMSUser.getAllByLMS(lmsID)
			.then((result) => {
				if (result) {
					switch (result.status) {
						case 200:
							setUsers(result.data);
							break;
						case 400:
							setAlertType("warning");
							setAlert(strings.requestFailed);
							break;
						case 401:
							logout();
							break;
						case 403:
							setAlertType("danger");
							setAlert(strings.unauthorisedRequest);
							break;
						default:
					}
				}
			});
	}, [lmsID, logout]);

	useEffect(() => {
		if (adminID) {
			if (role === "superadmin") {
				Admin.get(adminID)
					.then((result) => {
						switch (result.status) {
							case 200:
								setAdminLabel((result.data) ? `${result.data.username} (${result.data.email})` : "");
								setIsLoading(false);
								break;
							case 401:
								logout();
								break;
							case 404:
								setAlertType("warning");
								setAlert(strings.requestFailed);
								break;
							case 403:
								setAlertType("danger");
								setAlert(strings.unauthorisedRequest);
								break;
							case 500:
								setAlertType("danger");
								setAlert(strings.serverError);
								break;
							default:
						}
					});
			} else {
				setIsLoading(false);
			}
		}
	}, [adminID, logout, role]);

	useEffect(() => {
		const found = users.filter((user) => {
			const keysToFilterOut = ["id"];
			const filteredUser = Object.fromEntries(Object.entries(user).filter(([key]) => !keysToFilterOut.includes(key)));
			return Object.values(filteredUser)
				.some((value) => value.toString().toLowerCase().includes(searchTerm.toLowerCase()));
		});
		setFoundUsers(found);

		setTotalPages(Math.ceil(found.length / itemsPerPage));

		if (searchTermRef.current !== undefined && searchTermRef.current !== searchTerm) {
			searchTermRef.current = searchTerm;
			setCurrentPage(1);
		}
	}, [users, searchTerm]);

	useEffect(() => {
		if (totalPages > 0 && currentPage > totalPages) {
			setCurrentPage(totalPages);
		}
	}, [currentPage, totalPages]);

	useEffect(() => {
		setAccountContext({
			currentPage: name,
			breadcrumbs: [
				{
					label: strings.uloi,
					url: "/"
				},
				{
					label: strings.lms,
					url: "/dashboard",
				},
				{
					label: name
				}
			]
		});
	}, [setAccountContext, name]);

	return (
		(isLoading) ?
			<Spinner animation="border" className="spinner" />
		:
			<>
				<Button type="button" className="standard-dark-button ms-2 ms-sm-0" onClick={back}>
					<img alt="<" src="/images/back.svg" />
					<span className="align-middle ms-3">{strings.back}</span>
				</Button>

				<Container id="details-container" className="mt-4">
					<Row className="pt-3 pb-1">
						<Col className="user-select-all transparent-cursor">{name}</Col>
					</Row>
					{
						(alert) &&
							<Row className="mt-3">
								<Col>
									<Alert variant={alertType} className="mb-0 user-select-none transparent-cursor" dismissible={true} onClose={closeAlert}>{alert}</Alert>
								</Col>
							</Row>
					}
					<Row className="mt-3">
						<Col className="col-12 col-md-8 truncated-text">
							<span className="user-select-none transparent-cursor fw-bold">{strings.url}: </span>
							<span className="break-line break-all user-select-all transparent-cursor">{url}</span>
							<br />
							<span className="user-select-none transparent-cursor fw-bold">{strings.type}: </span>
							<span className="user-select-all transparent-cursor">{type}</span>
							<br />
							<span className="user-select-none transparent-cursor fw-bold">{strings.lmsID}: </span>
							<span className="user-select-all transparent-cursor">{lmsID}</span>
							{
								(role === "superadmin") &&
									<>
										<br />
										<span className="user-select-none transparent-cursor fw-bold">{strings.administrator}: </span>
										<span className="user-select-all transparent-cursor">{adminLabel}</span>
									</>
							}
						</Col>
						<Col className="col-12 col-md-4 mt-2 mt-md-0 text-end">
							<Row>
								<Col>
									<Button type="button" className="edit-button" onClick={() => setShowEditLMSModal(true)}>
										<img alt="" src="/images/edit.svg" />
										<span className="align-middle fw-bold ms-2 d-none d-xl-inline">{strings.editLMS}</span>
									</Button>
									<Button type="button" className="delete-button ms-2 ms-xl-3" onClick={() => setShowDeleteLMSModal(true)}>
										<img alt="" src="/images/delete.svg" />
										<span className="align-middle fw-bold ms-2 d-none d-xl-inline">{strings.deleteLMS}</span>
									</Button>
								</Col>
							</Row>
							<Row className="mt-3 mt-md-5">
								<Col>
									<Form.Control id="search-box" className="float-end" type="text" placeholder={strings.search} value={searchTerm} onChange={search} autoFocus={true} />
								</Col>
							</Row>
						</Col>
					</Row>

					<Row className="mt-3">
						<Col>
							<Table responsive striped>
								<thead>
									<tr>
									{
										tableColumnNames.map((columnName, index) => (
											<th key={index} className={`w-33 user-select-none transparent-cursor${(index === 0) ? " ps-3" : ""}`}>{columnName}</th>
										))	
									}
									</tr>
								</thead>
								<tbody>
								{
									(usersOnCurrentPage.length === 0) ?
										<tr>
											<td className="py-3 text-center user-select-none transparent-cursor" colSpan="4">{strings.noResults}</td>
										</tr>
									:
										usersOnCurrentPage.map((user, index) => (
											<tr key={index}>
												<td className="ps-3 user-select-all">{user.idUsr}</td>
												<td className="user-select-all">{user.idApp3D}</td>
												<td>
													<Button type="button" className="delete-button" onClick={() => setDeleteUserModalState(user)}>
														<img alt="" src="/images/delete.svg" />
														<span className="align-middle ms-2 d-none d-xl-inline">{strings.delete}</span>
													</Button>
												</td>
											</tr>
										))
								}
								</tbody>
							</Table>
							{
								(totalPages > 0) &&
									<Pagination className="d-flex justify-content-center">
										<Pagination.First className="user-select-none transparent-cursor" onClick={() => setCurrentPage(1)} />
										<Pagination.Prev className="user-select-none transparent-cursor" onClick={() => setCurrentPage((currentPage > 1) ? (currentPage - 1) : 1)} />
										{
											[...Array(totalPages)].map((_, index) => (
												<Pagination.Item className="user-select-none transparent-cursor" key={index + 1} active={(index + 1) === currentPage} onClick={() => setCurrentPage(index + 1)}>
													{index + 1}
												</Pagination.Item>
											))
										}
										<Pagination.Next className="user-select-none transparent-cursor" onClick={() => setCurrentPage((currentPage < totalPages) ? (currentPage + 1) : totalPages)} />
										<Pagination.Last className="user-select-none transparent-cursor" onClick={() => setCurrentPage(totalPages)} />
									</Pagination>
							}
						</Col>
					</Row>
				</Container>

				<EditLMSView show={showEditLMSModal} setShow={setShowEditLMSModal} id={lmsID} name={name} type={type} adminID={adminID} url={url} showUsers={{setName, setType, setAdminID, setAdminLabel, setURL}} />
				<ConfirmationBox show={showDeleteLMSModal} setShow={setShowDeleteLMSModal} action={removeLMS} />
				<ConfirmationBox show={deleteUserModalState} setShow={setDeleteUserModalState} action={() => removeLMSUser(...Object.values(deleteUserModalState))} />
			</>
	)
}

export default ShowUsersView;
